// Client-level Time Entries — read-only view across all matters
// No create/timer — those live at the matter level

import React from 'react';
import type { TimeEntry } from '../../../../billing/types/billing.types';

interface ClientTimeSectionProps {
  timeEntries: TimeEntry[];
  loading: boolean;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const SkeletonRow: React.FC = () => (
  <tr>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-24" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-48" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-10 ml-auto" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-16 ml-auto" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-16 ml-auto" /></td>
    <td className="px-3 py-2 text-center"><div className="lex-skeleton h-5 w-16 mx-auto rounded-full" /></td>
  </tr>
);

const ClientTimeSection: React.FC<ClientTimeSectionProps> = ({ timeEntries, loading }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Time Entries
          {!loading && (
            <span className="text-sm font-normal text-gray-400 ml-2">({timeEntries.length})</span>
          )}
        </h4>
        <p className="text-xs text-gray-400 dark:text-gray-500">Log time from individual matter pages</p>
      </div>

      {loading ? (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Hours</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Rate</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      ) : timeEntries.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          No time entries for this client yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Hours</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Rate</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {timeEntries.map((te, i) => (
                <tr key={te.time_entry_id} className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50" style={{ '--row-index': i } as React.CSSProperties}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{formatDate(te.entry_date)}</td>
                  <td className="px-3 py-2 text-lex-accent whitespace-nowrap text-xs">
                    {(te as any).matter?.description || (te as any).matter?.matter_number || (te.matter_id ? te.matter_id.slice(0, 8) : '-')}
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">{te.description}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white tabular-nums">{Number(te.duration).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                    {te.rate != null ? formatCurrency(Number(te.rate)) : '-'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium tabular-nums">{formatCurrency(Number(te.amount))}</td>
                  <td className="px-3 py-2 text-center">
                    {te.is_billed ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">Billed</span>
                    ) : te.is_non_billable ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Non-billable</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">Unbilled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientTimeSection;
